/**
 * useDisclosureFilters Hook
 *
 * Manages filter state for the disclosure dashboard
 */

import { useState, useCallback, useMemo } from 'react';
import type { DisclosureFilters } from '@/types/disclosure-dashboard';
import { DEFAULT_DISCLOSURE_FILTERS } from '@/types/disclosure-dashboard';

interface UseDisclosureFiltersResult {
  filters: DisclosureFilters;
  setFilter: <K extends keyof DisclosureFilters>(key: K, value: DisclosureFilters[K]) => void;
  setFilters: (filters: Partial<DisclosureFilters>) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
}

export function useDisclosureFilters(): UseDisclosureFiltersResult {
  const [filters, setFiltersState] = useState<DisclosureFilters>(DEFAULT_DISCLOSURE_FILTERS);

  const setFilter = useCallback(<K extends keyof DisclosureFilters>(
    key: K,
    value: DisclosureFilters[K]
  ) => {
    setFiltersState(prev => ({ ...prev, [key]: value }));
  }, []);

  const setFilters = useCallback((newFilters: Partial<DisclosureFilters>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_DISCLOSURE_FILTERS);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.lockStatus !== 'all' ||
      filters.itopStatus !== 'all' ||
      filters.hideCompleted !== DEFAULT_DISCLOSURE_FILTERS.hideCompleted ||
      filters.loanOfficerId !== undefined
    );
  }, [filters]);

  return {
    filters,
    setFilter,
    setFilters,
    resetFilters,
    hasActiveFilters
  };
}
